import { arCountry } from './ar';
import { mxCountry } from './mx';
import type { CountryCode, CountryConfig } from './types';

export type { CountryCode, CountryConfig, CountryFeature } from './types';

export const COUNTRIES: Record<CountryCode, CountryConfig> = {
  AR: arCountry,
  MX: mxCountry,
};

export const SUPPORTED_COUNTRIES: CountryCode[] = ['AR', 'MX'];

export function getCountry(code: CountryCode): CountryConfig {
  return COUNTRIES[code];
}

export function isSupportedCountry(code: string): code is CountryCode {
  return code === 'AR' || code === 'MX';
}

/**
 * Display an IVA rate in the country's locale convention. Non-integer
 * rates use a comma (e.g. AR's `10.5` → `"10,5"`); integers stay plain
 * (`16`, `21`). UI labels typically wrap with `IVA ${rate}%`.
 */
export function formatIvaRate(rate: number): string {
  if (Number.isInteger(rate)) return String(rate);
  return rate.toString().replace('.', ',');
}

/** MX SAT IVA classification. Math is identical across both — only the
 *  CFDI 4.0 classification and the IVA Compras book grouping differ. */
export type IvaCategoria = 'general' | 'importacion';

/** A single selectable IVA option as rendered in a compras-line picker.
 *  `value` is the dropdown's stable string key (e.g. `"16-importacion"`);
 *  `rate` is the numeric percent used for math; `categoria` carries the
 *  SAT classification. */
export type IvaOption = {
  value: string;
  label: string;
  rate: number;
  categoria: IvaCategoria;
};

/**
 * IVA options to render in the **compras** line picker for a country.
 * Returns the base rates (general) plus MX-only "16% Importación" as a
 * separate entry sharing the 16% rate but tagged `categoria='importacion'`.
 * AR returns only general entries. The 0% Exento row is rendered
 * separately by the caller because it gates on `llevaFactura`, not
 * country.
 */
export function getCompraIvaOptions(country: CountryConfig): IvaOption[] {
  const base = country.ivaRates.map<IvaOption>((rate) => ({
    value: String(rate),
    label: `IVA ${formatIvaRate(rate)}%`,
    rate,
    categoria: 'general',
  }));
  if (country.code === 'MX') {
    // Insert immediately after the matching general 16% so the option
    // appears next to its sibling rather than at the end.
    const idx = base.findIndex((o) => o.rate === 16);
    const importacion: IvaOption = {
      value: '16-importacion',
      label: 'IVA 16% Importación',
      rate: 16,
      categoria: 'importacion',
    };
    if (idx >= 0) base.splice(idx + 1, 0, importacion);
    else base.push(importacion);
  }
  return base;
}

/** Inverse helper: turn a stored `(rate, categoria)` pair back into the
 *  dropdown `value` so the Select can pre-select the right item when
 *  editing a line. */
export function ivaOptionValueFor(
  rate: number,
  categoria: IvaCategoria | null | undefined,
): string {
  if (categoria === 'importacion' && rate === 16) return '16-importacion';
  return String(rate);
}
